import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import type { AppwriteSession } from "../lib/appwrite";
import {
  API_URL,
  apiFetch,
  clearCachedJwt,
  seedCachedJwt,
  sweepLegacySessionSecretStorage,
} from "../lib/api";
import type { Permission, Role } from "@emr/shared";

export interface AuthProfile {
  id: string;
  fullName: string;
  role: Role;
  theme: "clinic-cream" | "clinic-dark";
  isActive: boolean;
  mustChangePassword: boolean;
}

interface AuthContextValue {
  user: { id: string; email?: string } | null;
  profile: AuthProfile | null;
  permissions: Set<string>;
  permissionList: Permission[];
  session: AppwriteSession | null;
  loading: boolean;
  isAdmin: boolean;
  mustChangePassword: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfileName: (name: string) => Promise<void>;
  updateProfileTheme: (theme: "clinic-cream" | "clinic-dark") => Promise<void>;
  refreshProfile: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface ProfileResponse {
  profile: AuthProfile;
  permissions: string[];
}

/** /auth/refresh payload (profile + perms + fresh JWT + session meta). */
interface RefreshResponse {
  profile: AuthProfile;
  permissions: string[];
  jwt: string;
  session: { userId: string; expire: string };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AppwriteSession | null>(null);
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [permissionList, setPermissionList] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      const data = await apiFetch<ProfileResponse>("/profile");
      setProfile(data.profile);
      setPermissions(new Set(data.permissions));
      // permissionList is loaded separately by the permissions catalog fetch when
      // needed by the UI; here we just store keys.
      setPermissionList([]);
    } catch (e) {
      // Token invalid or profile missing — clear.
      setProfile(null);
      setPermissions(new Set());
    }
  }, []);

  // On mount: probe the server for a live session (2026-09-24 cookie
  // transport). The session secret lives ONLY in the httpOnly cookie set by
  // /auth/login — JavaScript cannot read it, so the old "read localStorage to
  // decide whether to attempt a restore" design is gone. Instead POST
  // /auth/refresh with an empty body: the server reads the cookie and either
  // returns profile+permissions+fresh JWT (signed in) or 401 (not signed
  // in). The Worker path has no cookie jar and cannot be shadowed by the
  // stale a_session_* cookies on fra.cloud.appwrite.io (measured
  // 2026-09-21; see SCHEMA.md §18). The in-memory JWT is the client-side
  // session indicator.
  //
  // BARE FETCH, NOT apiFetch (deliberate — mirrors signOut's 2026-09-22
  // rationale): (a) apiFetch calls getJwt() first, which would fire its own
  // /auth/refresh mint and duplicate this probe; (b) apiFetch's 401 handler
  // toasts "Not signed in" — unacceptable for the signed-out page-load
  // probe, where 401 is the NORMAL answer, not an incident.
  const restoreSession = useCallback(async (): Promise<AppwriteSession | null> => {
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Same-origin + credentials → the httpOnly session cookie rides
        // automatically (prod: same Worker; dev: the Vite /api proxy
        // forwards Cookie headers to the Worker).
        credentials: "include",
        // Empty body: the server resolves the secret from the cookie (the
        // body `sessionSecret` field is a pre-deploy-bundle migration path).
        body: JSON.stringify({}),
        signal: AbortSignal.timeout(10_000),
      });
      if (!res.ok) {
        // 401 = not signed in (cookie absent / session dead) — the normal
        // signed-out state. 403/5xx/timeout = transient trouble — the
        // server-managed cookie survives, so the next reload retries the
        // restore. No local credential state exists to preserve or wipe.
        return null;
      }
      const data = (await res.json()) as RefreshResponse;
      seedCachedJwt(data.jwt); // later apiFetch calls reuse this token
      setProfile(data.profile);
      setPermissions(new Set(data.permissions));
      return {
        $id: "current",
        $createdAt: new Date().toISOString(),
        $updatedAt: new Date().toISOString(),
        userId: data.session.userId,
        expire: data.session.expire,
        provider: "email",
        providerUid: data.profile.id,
      };
    } catch {
      return null; // network error / timeout — retry on next mount
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      const restored = await restoreSession();
      if (!mounted) return;
      setSession(restored);
      setLoading(false);
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Combined login (2026-09-19): POST /auth/login does everything server-side
  // in ONE request — Appwrite session + JWT mint + profile/permissions load —
  // and returns all three. The 2026-09-21 re-auth fix routes ALL later
  // session/JWT operations through Worker routes too (/auth/refresh,
  // /auth/logout); browser->Appwrite SDK calls are never used for auth
  // (stale-cookie shadowing, see restoreSession note).
  // 2026-09-24: the session secret arrives ONLY as the Worker-set httpOnly
  // cookie on the login response — the SPA never sees or stores it (Appwrite
  // SSR pattern). The response body carries profile/permissions/JWT only.
  // NOTE: apiFetch's 401-retry is inert here (no cached JWT at login → token
  // is null → no retry), so a wrong password hits Appwrite exactly once.
  const signIn = useCallback(
    async (email: string, password: string) => {
      let data: ProfileResponse & {
        jwt: string;
        session: { userId: string; expire: string };
      };
      try {
        data = await apiFetch<ProfileResponse & {
          jwt: string;
          session: { userId: string; expire: string };
        }>("/auth/login", {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({ email, password }),
        });
      } catch (e) {
        const err = e as Error & { status?: number };
        // Friendly message for the user; original error attached as `cause`
        // so server-side detail survives for debugging (preserve-caught-error).
        if (err.status === 401) {
          throw new Error("Invalid email or password", { cause: e });
        } else if (err.status === 403) {
          throw new Error("Account deactivated", { cause: e });
        } else if (err.status === 429) {
          throw new Error("Too many attempts — try again shortly", { cause: e });
        } else if (err.status === 503) {
          throw new Error("Service temporarily unavailable — please try again in a few minutes", { cause: e });
        } else if (err.status !== undefined) {
          throw new Error("Sign-in failed. Please try again.", { cause: e });
        } else {
          throw e; // network error — surface as-is
        }
      }
      // The httpOnly session cookie was set by the login response — nothing
      // to persist client-side. Seed the JWT cache (skip the first re-mint,
      // ~300ms) and hydrate React state.
      seedCachedJwt(data.jwt); // skip the first re-mint (~300ms)
      setSession({
        $id: "current",
        $createdAt: new Date().toISOString(),
        $updatedAt: new Date().toISOString(),
        userId: data.session.userId,
        expire: data.session.expire,
        provider: "email",
        providerUid: email,
      });
      setProfile(data.profile);
      setPermissions(new Set(data.permissions));
    },
    [],
  );

  const signOut = useCallback(async () => {
    // Delete the Appwrite session THROUGH THE WORKER, by cookie. The
    // session secret lives in the httpOnly cookie (2026-09-24) — the server
    // reads it; this fetch just needs to be same-origin so the cookie rides
    // along. The browser SDK's deleteSession("current") was shadowed by
    // stale a_session_* cookies at Appwrite and failed silently — sessions
    // accumulated (8 observed 2026-09-21).
    //
    // CRITICAL (2026-09-22): call /auth/logout with a bare fetch, NOT
    // apiFetch. apiFetch calls getJwt() before every request, and getJwt()
    // calls /auth/refresh which does a DB query (profile + permissions). If
    // the DB/Hyperdrive is hanging (2026-09-21 outage), getJwt() hangs, so
    // apiFetch hangs, so signOut() hangs, so the user refreshes the tab and
    // the logout request is aborted — the Appwrite session is never deleted
    // and accumulates (10 sessions observed, 8 after the fix deployed).
    // /auth/logout is a PUBLIC route (no auth middleware) and needs NO JWT.
    // Bypassing apiFetch means logout always completes in ~1 round trip
    // regardless of DB/JWT state, so sessions are reliably deleted.
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        // Empty body: the server reads the secret from the httpOnly cookie
        // (body `sessionSecret` remains a pre-deploy-bundle fallback).
        body: JSON.stringify({}),
        // Don't let a hung Worker block sign-out indefinitely — 8s is
        // generous for a single Appwrite DELETE and well under the
        // browser's default 300s fetch timeout.
        signal: AbortSignal.timeout(8_000),
      });
    } catch {
      // Session may already be gone, or the Worker is unreachable —
      // client state clears regardless. The orphaned session (if any)
      // expires at Appwrite's 1-day session policy; the user can
      // force-logout from the admin users page if needed.
    }
    // The server cleared the httpOnly cookie on the logout response. Clear
    // the in-memory JWT + React state, and sweep any leftover localStorage
    // secret entries from pre-cookie-deploy bundles (emr:sessionSecret and
    // the legacy cookieFallback copy) — no credential may survive sign-out.
    sweepLegacySessionSecretStorage();
    clearCachedJwt(); // never reuse the old session's JWT after sign-out
    setSession(null);
    setProfile(null);
    setPermissions(new Set());
  }, []);

  const updateProfileName = useCallback(async (name: string) => {
    await apiFetch("/profile", {
      method: "PATCH",
      body: JSON.stringify({ fullName: name }),
    });
    setProfile((p) => (p ? { ...p, fullName: name } : p));
  }, []);

  const updateProfileTheme = useCallback(
    async (theme: "clinic-cream" | "clinic-dark") => {
      await apiFetch("/profile", {
        method: "PATCH",
        body: JSON.stringify({ theme }),
      });
      setProfile((p) => (p ? { ...p, theme } : p));
    },
    [],
  );

  const refreshProfile = useCallback(async () => {
    await fetchProfile();
  }, [fetchProfile]);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      await apiFetch("/auth/change-password", {
        method: "POST",
        // The caller's current session is identified server-side from the
        // httpOnly session cookie (2026-09-24) — the same possession pattern
        // /auth/refresh + /auth/logout use; no sessionSecret travels in the
        // body anymore. If the cookie is absent/dead, the Worker fails
        // closed and deletes ALL sessions — the user just re-signs-in.
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setProfile((p) => (p ? { ...p, mustChangePassword: false } : p));
      await refreshProfile();
    },
    [refreshProfile],
  );

  // Display identity. The Appwrite SDK account.get() used to provide the
  // email, but that call is subject to the stale-cookie shadowing; the
  // profile's full name is shown instead (it is what the topbar displays).
  const displayEmail = profile?.fullName;
  const user = session ? { id: session.userId, email: displayEmail } : null;
  const isAdmin = profile?.role === "admin";
  const mustChangePassword = profile?.mustChangePassword ?? false;

  const value: AuthContextValue = {
    user,
    profile,
    permissions,
    permissionList,
    session,
    loading,
    isAdmin,
    mustChangePassword,
    signIn,
    signOut,
    updateProfileName,
    updateProfileTheme,
    refreshProfile,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
